import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw, Download, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const Ctx = createContext({ markDirty: () => {}, isDirty: () => false });
export const useDirtyForms = () => useContext(Ctx);
const POLL_MS = 5 * 60 * 1000;

const fetchVersion = () => fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" }).then((r) => (r.ok ? r.json() : null)).catch(() => null);

export function AppShellProvider({ children }) {
  const dirty = useRef(new Set());
  const markDirty = useCallback((id, on) => { if (on) dirty.current.add(id); else dirty.current.delete(id); }, []);
  const isDirty = useCallback(() => dirty.current.size > 0, []);
  useEffect(() => {
    const h = (e) => { if (dirty.current.size) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);
  return <Ctx.Provider value={{ markDirty, isDirty }}>{children}</Ctx.Provider>;
}

export function useMarkDirty(id, on) {
  const { markDirty } = useDirtyForms();
  useEffect(() => { markDirty(id, on); return () => markDirty(id, false); }, [id, on, markDirty]);
}

export function RefreshButton({ compact }) {
  const qc = useQueryClient();
  const { refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await Promise.all([qc.invalidateQueries({ refetchType: "active" }), refreshUser().catch(() => {})]);
      toast.success("Updated", { duration: 1500 });
    } catch { toast.error("Could not refresh. Check your connection."); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={refresh} disabled={busy} aria-label="Refresh data" title="Refresh data" data-testid={compact ? "mobile-refresh-button" : "refresh-button"}
      className={`inline-flex items-center gap-1.5 rounded-lg text-sm font-semibold transition-colors ${compact ? "w-11 h-11 justify-center hover:bg-white/10 text-white" : "px-3 h-9 text-slate-300 hover:text-white hover:bg-white/10"}`}>
      <RefreshCw size={compact ? 18 : 15} className={busy ? "animate-spin" : ""} />{!compact && <span>{busy ? "Refreshing…" : "Refresh"}</span>}
    </button>
  );
}

export function UpdateBanner() {
  const { isDirty } = useDirtyForms();
  const [current, setCurrent] = useState(null);
  const [latest, setLatest] = useState(null);
  const [dismissedFor, setDismissedFor] = useState(() => sessionStorage.getItem("neec_update_dismissed") || "");
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchVersion().then((v) => alive && v && setCurrent(v.version));
    const check = async () => { const v = await fetchVersion(); if (alive && v?.version) setLatest(v.version); };
    const id = setInterval(check, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { alive = false; clearInterval(id); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
  }, []);
  const available = current && latest && latest !== current && dismissedFor !== latest;
  if (!available) return null;
  const reload = () => { sessionStorage.removeItem("neec_update_dismissed"); window.location.reload(); };
  const update = () => (isDirty() ? setConfirm(true) : reload());
  const dismiss = () => { sessionStorage.setItem("neec_update_dismissed", latest); setDismissedFor(latest); };
  return (
    <>
      <div className="fixed bottom-24 md:bottom-4 left-3 right-3 md:left-1/2 md:right-auto md:-translate-x-1/2 z-40 md:w-full md:max-w-md rounded-xl bg-[#0B192C] text-white shadow-2xl px-4 py-3 flex flex-wrap items-center gap-2 fade-up" role="status" data-testid="update-banner">
        <Download size={18} className="text-orange-400 shrink-0" />
        <span className="text-sm flex-1 min-w-[140px]">An update is available.</span>
        <button type="button" onClick={dismiss} aria-label="Dismiss" className="p-1 rounded hover:bg-white/10 order-last md:order-none" data-testid="update-dismiss-button"><X size={16} /></button>
        <button type="button" onClick={update} className="btn-orange rounded-lg px-3 h-9 text-sm font-semibold whitespace-nowrap" data-testid="update-refresh-button">Update &amp; Refresh</button>
      </div>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="You have unsaved changes" description="Updating reloads the app and any unsaved form changes will be lost. Save your work first, or continue to update now." confirmLabel="Update anyway" destructive onConfirm={reload} testId="update-unsaved-dialog" />
    </>
  );
}
